import { useState } from "react";
import { useAuth } from "../App";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, Sun, Moon, Shield } from "lucide-react";

// Microsoft logo SVG
const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const LoginPage = () => {
  const { login } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    try {
      await login();
      // Si on arrive ici, la redirection vers Keycloak est en cours
      // La page va naviguer — isLoading reste true
    } catch (err) {
      setIsLoading(false);
      const code = err?.errorCode || "";
      if (code === "block_nested_popups") {
        toast.error(
          "Veuillez ouvrir cette page dans un onglet principal (pas depuis un lien externe)."
        );
      } else if (code === "user_cancelled") {
        // silent — l'utilisateur a fermé la fenêtre
      } else {
        toast.error(err?.message || "Impossible de joindre Keycloak");
      }
    }
  };

  return (
    <div className="min-h-screen flex transition-colors duration-300">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white dark:bg-gray-900 relative">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="absolute top-4 right-4 p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
          aria-label="Basculer le thème"
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>

        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="text-center mb-10">
            <h1 className="font-['Playfair_Display'] text-4xl font-bold text-[#1A4D2E] dark:text-emerald-400 mb-2">
              WorkPulse
            </h1>
            <p className="text-[#4B5563] dark:text-gray-400">
              Gestion RH simplifiée pour votre équipe
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-8">
            <div className="text-center mb-8">
              <div className="w-14 h-14 bg-[#E8F5E9] dark:bg-emerald-900/40 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-[#1A4D2E] dark:text-emerald-400" />
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#111827] dark:text-white mb-1">
                Bienvenue
              </h2>
              <p className="text-sm text-[#6B7280] dark:text-gray-400">
                Connectez-vous avec votre compte professionnel ELIKA
              </p>
            </div>

            <Button
              onClick={handleMicrosoftLogin}
              disabled={isLoading}
              className="w-full h-12 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#111827] dark:text-white border border-gray-300 dark:border-gray-600 rounded-xl font-medium shadow-sm transition-all flex items-center justify-center gap-3"
              data-testid="microsoft-login-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <MicrosoftIcon />
                  <span>Se connecter</span>
                </>
              )}
            </Button>

            <p className="mt-6 text-center text-xs text-[#9CA3AF] dark:text-gray-500">
              Authentification unique ELIKA — Keycloak fédérant Microsoft Entra ID
            </p>
          </div>

          <p className="mt-6 text-center text-xs text-[#9CA3AF] dark:text-gray-600">
            Accès réservé aux collaborateurs de l'entreprise.
            <br />
            Contactez votre administrateur en cas de problème.
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div
        className="hidden lg:flex flex-1 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/3754595/pexels-photo-3754595.jpeg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A4D2E]/90 to-[#143D24]/80" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white max-w-lg mx-auto">
          <blockquote className="text-3xl font-['Playfair_Display'] leading-relaxed mb-6">
            "Investir dans vos collaborateurs, c'est investir dans la croissance de votre entreprise."
          </blockquote>
          <p className="text-white/80">— WorkPulse</p>

          <div className="mt-12 grid grid-cols-2 gap-6">
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold">100%</p>
              <p className="text-sm text-white/70">Processus digitalisés</p>
            </div>
            <div className="p-4 rounded-xl bg-white/10 backdrop-blur">
              <p className="text-3xl font-bold">24/7</p>
              <p className="text-sm text-white/70">Accès à vos données</p>
            </div>
          </div>

          {/* Security badge */}
          <div className="mt-8 flex items-center gap-3 p-4 rounded-xl bg-white/10 backdrop-blur">
            <div className="w-10 h-10 flex-shrink-0">
              <MicrosoftIcon />
            </div>
            <div>
              <p className="font-medium text-sm">Microsoft Entra ID</p>
              <p className="text-xs text-white/70">Authentification d'entreprise sécurisée</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
